import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { caseAPI, clientAPI, visaProgramAPI, userAPI, staffAPI, contractorPaymentAPI, api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  Briefcase, Filter, Eye, ArrowLeft, PauseCircle, AlertTriangle,
  Search, Trash2, Edit, MoreVertical, X, CheckCircle, XCircle,
  Clock, Calendar, User, FileText, DollarSign, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

const STATUS_FILTERS = [
  { value: 'all', label: 'All Cases', icon: Briefcase },
  { value: 'active', label: 'Active', icon: Clock },
  { value: 'suspended', label: 'Suspended', icon: PauseCircle },
  { value: 'expired', label: 'Expired', icon: AlertTriangle },
  { value: 'completed', label: 'Completed', icon: CheckCircle },
  { value: 'approved', label: 'Approved', icon: CheckCircle },
  { value: 'rejected', label: 'Rejected', icon: XCircle },
];

export default function AdminCases() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState({});
  const [programs, setPrograms] = useState({});
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Contractor payment dialog
  const [contractorDialog, setContractorDialog] = useState(null);
  const [contractorPayForm, setContractorPayForm] = useState({ paymentStatus: 'unpaid', method: 'Cash', reference: '' });
  const [savingContractorPay, setSavingContractorPay] = useState(false);
  const [staffMap, setStaffMap] = useState({});
  const [casePaymentMap, setCasePaymentMap] = useState({}); // case_id → contractor_payment record
  const [markPayModal, setMarkPayModal] = useState(null); // { payment }
  const [markPayForm, setMarkPayForm] = useState({ method: 'Cash', reference: '' });
  const [markPaySaving, setMarkPaySaving] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const urlFilter = searchParams.get('filter');
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'process_manager';
  const canDeleteCases = user?.role === 'super_admin' || user?.role === 'process_manager';
  const isOfficer = user?.role === 'case_officer';

  const getCaseDetailPath = (caseId) => {
    switch (user?.role) {
      case 'super_admin':
        return `/admin/cases/${caseId}`;
      case 'process_manager':
        return `/manager/cases/${caseId}`;
      case 'case_officer':
        return `/officer/cases/${caseId}`;
      default:
        return `/admin/cases/${caseId}`;
    }
  };

  const getBackPath = () => {
    switch (user?.role) {
      case 'super_admin':
        return '/admin';
      case 'process_manager':
        return '/manager';
      case 'case_officer':
        return '/officer';
      default:
        return '/admin';
    }
  };

  useEffect(() => {
    if (urlFilter) {
      setStatusFilter(urlFilter);
    }
    fetchData();
  }, [urlFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isOfficer && urlFilter) {
        try {
          const filteredRes = await api.get(`/officer/cases-filtered?filter=${urlFilter}`);
          setCases(filteredRes.data);
        } catch (err) {
          const casesRes = await caseAPI.getCases();
          setCases(casesRes.data);
        }
      } else {
        const casesRes = await caseAPI.getCases();
        setCases(casesRes.data);
      }
      
      const [clientsRes, programsRes] = await Promise.all([
        clientAPI.getClients(),
        visaProgramAPI.getPrograms()
      ]);
      
      const clientsMap = {};
      clientsRes.data.forEach(c => clientsMap[c.id] = c);
      setClients(clientsMap);
      
      const programsMap = {};
      programsRes.data.forEach(p => programsMap[p.id] = p);
      setPrograms(programsMap);
      
      try {
        const [usersRes, staffRes, cpRes] = await Promise.all([
          userAPI.getUsers(),
          staffAPI.getStaff(),
          contractorPaymentAPI.getAll().catch(() => ({ data: [] })),
        ]);
        setOfficers(usersRes.data.filter(u => u.role === 'case_officer'));
        const map = {};
        (staffRes.data || []).forEach(s => { map[s.id] = s; });
        setStaffMap(map);
        const payMap = {};
        (cpRes.data || []).forEach(p => { payMap[p.case_id] = p; });
        setCasePaymentMap(payMap);
      } catch (err) {
        setOfficers([]);
      }
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignOfficer = async (caseId, officerId) => {
    if (!officerId) return;
    const targetCase = cases.find(c => c.id === caseId);
    const staffMember = staffMap[officerId];
    const comp = staffMember?.compensation;
    // Trigger dialog if officer has per-case rates OR per_case payment model (regardless of employment_type field)
    const isPerCase = comp?.contractor_payment_model === 'per_case' || (comp?.per_case_rates || []).length > 0;

    if (isPerCase) {
      // Find matching rate for this case's visa_type + country
      const matchingRate = (comp.per_case_rates || []).find(
        r => r.visa_type === targetCase?.visa_type && r.country === targetCase?.country
      ) || (comp.per_case_rates || [])[0];

      setContractorDialog({
        case: targetCase,
        officer: staffMember,
        rate: matchingRate || null,
        pendingOfficerId: officerId,
        comp,
      });
      setContractorPayForm({ paymentStatus: 'unpaid', method: 'Cash', reference: '' });
      return;
    }

    // Non-contractor — assign directly
    try {
      await caseAPI.updateCase(caseId, { assigned_officer_id: officerId });
      toast.success('Officer assigned successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign officer');
    }
  };

  const handleContractorAssignConfirm = async () => {
    if (!contractorDialog) return;
    setSavingContractorPay(true);
    const { case: targetCase, officer, rate, comp, logFeeOnly } = contractorDialog;
    try {
      // 1. Assign officer to case (skip if just logging fee for existing assignment)
      if (!logFeeOnly) {
        await caseAPI.updateCase(targetCase.id, { assigned_officer_id: officer.id });
      }

      // 2. Create contractor payment record + expense
      if (rate?.amount) {
        const isPaid = contractorPayForm.paymentStatus === 'paid';
        await contractorPaymentAPI.create({
          staff_id: officer.id,
          case_id: targetCase.id,
          amount: parseFloat(rate.amount),
          currency: rate.currency || 'INR',
          payment_terms: comp.payment_terms || null,
          payment_status: isPaid ? 'paid' : 'pending',
          paid_date: isPaid ? new Date().toISOString().split('T')[0] : null,
          paid_amount: isPaid ? parseFloat(rate.amount) : null,
          payment_method: isPaid ? contractorPayForm.method : null,
          payment_reference: contractorPayForm.reference || null,
          postpayment_trigger: comp.postpayment_trigger || null,
          trigger_stage: comp.trigger_stage || null,
        });
      }

      const isPaidMsg = contractorPayForm.paymentStatus === 'paid' ? 'logged as paid' : 'logged as pending in Accounting';
      toast.success(`Officer assigned! ${rate?.amount ? `₹${Number(rate.amount).toLocaleString('en-IN')} ${isPaidMsg}.` : ''}`);
      setContractorDialog(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete assignment');
    } finally {
      setSavingContractorPay(false);
    }
  };

  const handleMarkContractorPaid = async () => {
    if (!markPayModal) return;
    setMarkPaySaving(true);
    try {
      await contractorPaymentAPI.markPaid(markPayModal.payment.id, {
        payment_method: markPayForm.method,
        payment_reference: markPayForm.reference || null,
      });
      toast.success('Payment marked as paid');
      setMarkPayModal(null);
      // Refresh payment map
      const cpRes = await contractorPaymentAPI.getAll().catch(() => ({ data: [] }));
      const payMap = {};
      (cpRes.data || []).forEach(p => { payMap[p.case_id] = p; });
      setCasePaymentMap(payMap);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark paid');
    } finally { setMarkPaySaving(false); }
  };

  const handleMarkContractorUnpaid = async (paymentId, caseId) => {
    try {
      await contractorPaymentAPI.markUnpaid(paymentId);
      toast.success('Payment reverted to pending');
      setCasePaymentMap(prev => ({ ...prev, [caseId]: { ...prev[caseId], payment_status: 'pending' } }));
    } catch (e) {
      toast.error('Failed to revert payment');
    }
  };

  const handleStageChange = async (caseId, newStage) => {
    try {
      await caseAPI.updateCase(caseId, { stage: newStage });
      toast.success('Case stage updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleDeleteCase = async () => {
    if (!caseToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/cases/${caseToDelete.id}`);
      toast.success('Case deleted successfully');
      setShowDeleteModal(false);
      setCaseToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete case');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (case_) => {
    setCaseToDelete(case_);
    setShowDeleteModal(true);
  };

  const stages = [
    'New Client',
    'Agreement Sent',
    'Agreement Signed',
    'Documents Pending',
    'Documents Submitted',
    'Under Review',
    'Application Filed',
    'Decision Pending',
    'Approved',
    'Rejected',
    'Closed',
    'Suspended',
    'Expired'
  ];

  const getStageColor = (stage) => {
    const colors = {
      'New Client': 'bg-blue-100 text-blue-700',
      'Agreement Sent': 'bg-yellow-100 text-yellow-700',
      'Agreement Signed': 'bg-emerald-100 text-emerald-700',
      'Documents Pending': 'bg-amber-100 text-amber-700',
      'Documents Submitted': 'bg-teal-100 text-teal-700',
      'Under Review': 'bg-indigo-100 text-indigo-700',
      'Application Filed': 'bg-purple-100 text-purple-700',
      'Decision Pending': 'bg-cyan-100 text-cyan-700',
      'Approved': 'bg-green-100 text-green-700',
      'Rejected': 'bg-red-100 text-red-700',
      'Closed': 'bg-slate-100 text-slate-700',
      'Suspended': 'bg-orange-100 text-orange-700',
      'Expired': 'bg-red-100 text-red-700'
    };
    return colors[stage] || 'bg-slate-100 text-slate-700';
  };

  // Apply all filters
  const getFilteredCases = () => {
    let filtered = cases;

    // Apply status filter
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'active':
          filtered = filtered.filter(c => 
            !['Approved', 'Rejected', 'Closed', 'Suspended', 'Expired'].includes(c.stage) && !c.is_suspended
          );
          break;
        case 'suspended':
          filtered = filtered.filter(c => c.stage === 'Suspended' || c.is_suspended);
          break;
        case 'expired':
          filtered = filtered.filter(c => c.stage === 'Expired' || c.is_expired);
          break;
        case 'completed':
          filtered = filtered.filter(c => ['Approved', 'Rejected', 'Closed'].includes(c.stage));
          break;
        case 'approved':
          filtered = filtered.filter(c => c.stage === 'Approved');
          break;
        case 'rejected':
          filtered = filtered.filter(c => c.stage === 'Rejected');
          break;
        case 'unassigned':
          filtered = filtered.filter(c => !c.assigned_officer_id && !['Approved', 'Rejected', 'Closed'].includes(c.stage));
          break;
        default:
          break;
      }
    }

    // Apply stage filter
    if (selectedStage !== 'all') {
      filtered = filtered.filter(c => c.stage === selectedStage);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => {
        const client = clients[c.client_id];
        const program = programs[c.visa_program_id];
        return (
          c.id.toLowerCase().includes(query) ||
          client?.full_name?.toLowerCase().includes(query) ||
          client?.email?.toLowerCase().includes(query) ||
          program?.visa_type?.toLowerCase().includes(query) ||
          program?.country?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  };

  const filteredCases = getFilteredCases();

  // Calculate stats
  const stats = {
    total: cases.length,
    active: cases.filter(c => !['Approved', 'Rejected', 'Closed', 'Suspended', 'Expired'].includes(c.stage) && !c.is_suspended).length,
    suspended: cases.filter(c => c.stage === 'Suspended' || c.is_suspended).length,
    expired: cases.filter(c => c.stage === 'Expired' || c.is_expired).length,
    approved: cases.filter(c => c.stage === 'Approved').length,
    rejected: cases.filter(c => c.stage === 'Rejected').length,
    unassigned: cases.filter(c => !c.assigned_officer_id && !['Approved', 'Rejected', 'Closed'].includes(c.stage)).length,
  };

  // Officer workload map: officerId -> count of active cases
  const officerWorkload = {};
  officers.forEach(o => { officerWorkload[o.id] = 0; });
  cases.forEach(c => {
    if (c.assigned_officer_id && !['Approved', 'Rejected', 'Closed'].includes(c.stage)) {
      officerWorkload[c.assigned_officer_id] = (officerWorkload[c.assigned_officer_id] || 0) + 1;
    }
  });

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-300 border-t-slate-800"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="admin-cases-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            {urlFilter && (
              <button
                onClick={() => {
                  setStatusFilter('all');
                  navigate(getBackPath());
                }}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-2 text-sm transition-all"
              >
                <ArrowLeft size={16} />
                Back to Dashboard
              </button>
            )}
            <h1 className="text-2xl font-bold text-slate-900" data-testid="cases-title">
              Case Management
            </h1>
            <p className="text-slate-500 text-sm">
              {filteredCases.length} case{filteredCases.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <div className="flex items-center gap-3">
          </div>
        </div>

        {/* Stats Cards */}
        {isAdmin && (
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
            <button
              onClick={() => setStatusFilter('all')}
              className={`p-3 rounded-xl border transition-all text-left ${
                statusFilter === 'all' 
                  ? 'bg-slate-900 text-white border-slate-900' 
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <p className={`text-2xl font-bold ${statusFilter === 'all' ? 'text-white' : 'text-slate-900'}`}>
                {stats.total}
              </p>
              <p className={`text-xs font-medium ${statusFilter === 'all' ? 'text-slate-300' : 'text-slate-500'}`}>
                Total Cases
              </p>
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`p-3 rounded-xl border transition-all text-left ${
                statusFilter === 'active' 
                  ? 'bg-teal-600 text-white border-teal-600' 
                  : 'bg-white border-slate-200 hover:border-teal-300'
              }`}
            >
              <p className={`text-2xl font-bold ${statusFilter === 'active' ? 'text-white' : 'text-teal-600'}`}>
                {stats.active}
              </p>
              <p className={`text-xs font-medium ${statusFilter === 'active' ? 'text-teal-100' : 'text-slate-500'}`}>
                Active
              </p>
            </button>
            <button
              onClick={() => setStatusFilter('suspended')}
              className={`p-3 rounded-xl border transition-all text-left ${
                statusFilter === 'suspended' 
                  ? 'bg-orange-500 text-white border-orange-500' 
                  : 'bg-white border-slate-200 hover:border-orange-300'
              }`}
            >
              <p className={`text-2xl font-bold ${statusFilter === 'suspended' ? 'text-white' : 'text-orange-500'}`}>
                {stats.suspended}
              </p>
              <p className={`text-xs font-medium ${statusFilter === 'suspended' ? 'text-orange-100' : 'text-slate-500'}`}>
                Suspended
              </p>
            </button>
            <button
              onClick={() => setStatusFilter('expired')}
              className={`p-3 rounded-xl border transition-all text-left ${
                statusFilter === 'expired' 
                  ? 'bg-red-500 text-white border-red-500' 
                  : 'bg-white border-slate-200 hover:border-red-300'
              }`}
            >
              <p className={`text-2xl font-bold ${statusFilter === 'expired' ? 'text-white' : 'text-red-500'}`}>
                {stats.expired}
              </p>
              <p className={`text-xs font-medium ${statusFilter === 'expired' ? 'text-red-100' : 'text-slate-500'}`}>
                Expired
              </p>
            </button>
            <button
              onClick={() => setStatusFilter('approved')}
              className={`p-3 rounded-xl border transition-all text-left ${
                statusFilter === 'approved' 
                  ? 'bg-green-600 text-white border-green-600' 
                  : 'bg-white border-slate-200 hover:border-green-300'
              }`}
            >
              <p className={`text-2xl font-bold ${statusFilter === 'approved' ? 'text-white' : 'text-green-600'}`}>
                {stats.approved}
              </p>
              <p className={`text-xs font-medium ${statusFilter === 'approved' ? 'text-green-100' : 'text-slate-500'}`}>
                Approved
              </p>
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={`p-3 rounded-xl border transition-all text-left ${
                statusFilter === 'rejected'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white border-slate-200 hover:border-red-300'
              }`}
            >
              <p className={`text-2xl font-bold ${statusFilter === 'rejected' ? 'text-white' : 'text-red-600'}`}>
                {stats.rejected}
              </p>
              <p className={`text-xs font-medium ${statusFilter === 'rejected' ? 'text-red-100' : 'text-slate-500'}`}>
                Rejected
              </p>
            </button>
            <button
              onClick={() => setStatusFilter('unassigned')}
              className={`p-3 rounded-xl border transition-all text-left relative ${
                statusFilter === 'unassigned'
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white border-amber-200 hover:border-amber-400'
              }`}
            >
              {stats.unassigned > 0 && statusFilter !== 'unassigned' && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
                  !
                </span>
              )}
              <p className={`text-2xl font-bold ${statusFilter === 'unassigned' ? 'text-white' : 'text-amber-600'}`}>
                {stats.unassigned}
              </p>
              <p className={`text-xs font-medium ${statusFilter === 'unassigned' ? 'text-amber-100' : 'text-amber-600'}`}>
                Unassigned
              </p>
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by client name, email, case ID, or visa type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
              data-testid="case-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-500" />
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              data-testid="stage-filter"
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
            >
              <option value="all">All Stages</option>
              {stages.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Cases Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cases-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Case</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Visa Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Stage</th>
                  {!isOfficer && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Officer</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCases.length === 0 ? (
                  <tr>
                    <td colSpan={isOfficer ? 6 : 7} className="px-4 py-12 text-center">
                      <FileText size={48} className="mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500">No cases found matching your filters</p>
                    </td>
                  </tr>
                ) : (
                  filteredCases.map((case_) => {
                    const client = clients[case_.client_id];
                    const program = programs[case_.visa_program_id];
                    const officer = officers.find(o => o.id === case_.assigned_officer_id);
                    const isSuspended = case_.stage === 'Suspended' || case_.is_suspended;
                    const isExpired = case_.stage === 'Expired' || case_.is_expired;
                    const isUnassigned = !case_.assigned_officer_id && !['Approved', 'Rejected', 'Closed'].includes(case_.stage);

                    return (
                      <tr
                        key={case_.id}
                        className={`hover:bg-slate-50 transition-all ${isSuspended ? 'bg-orange-50/50' : ''} ${isExpired ? 'bg-red-50/50' : ''} ${isUnassigned ? 'border-l-4 border-l-amber-400' : ''}`}
                        data-testid={`case-row-${case_.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-bold text-teal-600">
                              {case_.ucin || `#${case_.id.slice(0, 8)}`}
                            </span>
                            {isSuspended && <PauseCircle size={14} className="text-orange-500" />}
                            {isExpired && <AlertTriangle size={14} className="text-red-500" />}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <button
                              onClick={() => navigate(getCaseDetailPath(case_.id))}
                              className="text-sm font-medium text-slate-900 hover:text-teal-600 hover:underline transition-colors text-left"
                              data-testid={`client-name-${case_.id}`}
                            >
                              {client?.full_name || 'N/A'}
                            </button>
                            <p className="text-xs text-slate-500">{client?.email || ''}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm text-slate-900">{program?.visa_type || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{program?.country || ''}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {isAdmin ? (
                            <select
                              value={case_.stage}
                              onChange={(e) => handleStageChange(case_.id, e.target.value)}
                              data-testid={`stage-select-${case_.id}`}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStageColor(case_.stage)} cursor-pointer border-0 outline-none`}
                            >
                              {stages.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          ) : (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStageColor(case_.stage)}`}>
                              {case_.stage}
                            </span>
                          )}
                        </td>
                        {!isOfficer && (
                          <td className="px-4 py-3">
                            {isAdmin && officers.length > 0 ? (
                              <div>
                                <select
                                  value={case_.assigned_officer_id || ''}
                                  onChange={(e) => handleAssignOfficer(case_.id, e.target.value)}
                                  data-testid={`officer-select-${case_.id}`}
                                  className={`px-2 py-1 text-xs rounded-lg border ${isUnassigned ? 'border-amber-300 bg-amber-50 text-amber-800' : 'bg-slate-50 border-slate-200'}`}
                                >
                                  <option value="">{isUnassigned ? '⚠ Assign Officer' : 'Unassigned'}</option>
                                  {officers.map(o => {
                                    const load = officerWorkload[o.id] || 0;
                                    const loadLabel = load >= 8 ? ' ⚠ High' : load >= 5 ? ' · Busy' : '';
                                    return (
                                      <option key={o.id} value={o.id}>
                                        {o.full_name} ({load} cases{loadLabel})
                                      </option>
                                    );
                                  })}
                                </select>
                                {/* Contractor payment status badge */}
                                {(() => {
                                  const cp = casePaymentMap[case_.id];
                                  const assignedStaff = staffMap[case_.assigned_officer_id];
                                  const isContractorPerCase = assignedStaff?.compensation?.contractor_payment_model === 'per_case'
                                    || (assignedStaff?.compensation?.per_case_rates || []).length > 0;

                                  if (cp) {
                                    return cp.payment_status === 'paid' ? (
                                      <div className="flex items-center gap-1 mt-1">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                          <CheckCircle size={10} /> Fee Paid
                                        </span>
                                        <button type="button" onClick={() => handleMarkContractorUnpaid(cp.id, case_.id)} className="text-xs text-slate-400 hover:text-slate-600 underline">revert</button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 mt-1">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                          <Clock size={10} /> Fee Pending
                                        </span>
                                        <button type="button" onClick={() => { setMarkPayModal({ payment: cp }); setMarkPayForm({ method: 'Cash', reference: '' }); }} className="text-xs text-green-600 hover:text-green-800 underline font-medium">mark paid</button>
                                      </div>
                                    );
                                  }

                                  // No record yet — show Log Fee button if officer is a per-case contractor
                                  if (isContractorPerCase && case_.assigned_officer_id) {
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const comp = assignedStaff.compensation;
                                          const matchingRate = (comp.per_case_rates || []).find(
                                            r => r.visa_type === case_.visa_type && r.country === case_.country
                                          ) || (comp.per_case_rates || [])[0];
                                          setContractorDialog({ case: case_, officer: assignedStaff, rate: matchingRate || null, comp, logFeeOnly: true });
                                          setContractorPayForm({ paymentStatus: 'unpaid', method: 'Cash', reference: '' });
                                        }}
                                        className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700 transition-all border border-slate-200 hover:border-amber-300"
                                      >
                                        <DollarSign size={9} /> Log Fee
                                      </button>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            ) : (
                              <span className="text-sm text-slate-600">
                                {officer?.full_name || 'Unassigned'}
                              </span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {new Date(case_.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => navigate(getCaseDetailPath(case_.id))}
                              data-testid={`view-case-${case_.id}`}
                              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                              title="View case details"
                            >
                              <Eye size={16} />
                            </button>
                            {canDeleteCases && (
                              <button
                                onClick={() => openDeleteModal(case_)}
                                data-testid={`delete-case-${case_.id}`}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                title="Delete case"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && caseToDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Delete Case</h3>
                    <p className="text-sm text-slate-500">This action cannot be undone</p>
                  </div>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-red-800">
                    Are you sure you want to delete case <strong>#{caseToDelete.id.slice(0, 8)}</strong>? 
                    This will permanently remove all associated documents, timeline, and data.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setCaseToDelete(null);
                    }}
                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteCase}
                    disabled={deleting}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-delete"
                  >
                    {deleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        Delete Case
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Mark Contractor Paid Modal ─────────────────────────────── */}
        {markPayModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign size={16} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Mark Fee as Paid</h3>
                    <p className="text-xs text-slate-500">{markPayModal.payment.staff_name} · ₹{Number(markPayModal.payment.amount).toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <button type="button" onClick={() => setMarkPayModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Payment Method</label>
                  <select value={markPayForm.method} onChange={e => setMarkPayForm(f => ({ ...f, method: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white outline-none">
                    {['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Other'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Reference / UTR (optional)</label>
                  <input type="text" value={markPayForm.reference} onChange={e => setMarkPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="e.g. UTR123456" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none" />
                </div>
              </div>
              <div className="px-5 py-4 border-t border-slate-200 flex gap-3">
                <button type="button" onClick={() => setMarkPayModal(null)} className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">Cancel</button>
                <button type="button" onClick={handleMarkContractorPaid} disabled={markPaySaving} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {markPaySaving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Confirm Paid
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Contractor Payment Dialog ───────────────────────────────── */}
        {contractorDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-5 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <DollarSign size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">{contractorDialog.logFeeOnly ? 'Log Contractor Fee' : 'Contractor Fee'}</h2>
                    <p className="text-xs text-slate-500">{contractorDialog.officer?.full_name} · Independent Contractor</p>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Case + Rate summary */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Case</span>
                    <span className="font-medium text-slate-800">{contractorDialog.case?.ucin || contractorDialog.case?.id?.slice(0,8)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Visa Program</span>
                    <span className="font-medium text-slate-800">{contractorDialog.case?.visa_type} · {contractorDialog.case?.country}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Contractor Fee</span>
                    <span className="font-bold text-slate-900">
                      {contractorDialog.rate ? `₹${Number(contractorDialog.rate.amount).toLocaleString('en-IN')}` : 'No rate configured'}
                    </span>
                  </div>
                </div>

                {contractorDialog.rate ? (
                  <>
                    {/* Paid / Unpaid toggle — always shown for per-case contractors */}
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Was this payment made?</p>
                      <div className="flex gap-3">
                        {[
                          { v: 'paid', label: '✓ Paid Now', color: 'bg-green-600 text-white border-green-600' },
                          { v: 'unpaid', label: '⏳ Unpaid / Log Pending', color: 'bg-amber-600 text-white border-amber-600' },
                        ].map(opt => (
                          <button key={opt.v} type="button"
                            onClick={() => setContractorPayForm(f => ({ ...f, paymentStatus: opt.v }))}
                            className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${contractorPayForm.paymentStatus === opt.v ? opt.color : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {contractorPayForm.paymentStatus === 'paid' ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Payment Method</label>
                          <select value={contractorPayForm.method} onChange={e => setContractorPayForm(f => ({ ...f, method: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-slate-900/20 outline-none">
                            {['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Other'].map(m => <option key={m}>{m}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Reference / UTR</label>
                          <input type="text" value={contractorPayForm.reference} onChange={e => setContractorPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="Optional" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900/20 outline-none" />
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        ₹{Number(contractorDialog.rate.amount).toLocaleString('en-IN')} will be logged as a <strong>pending expense</strong> in Accounting → Contractor Payables. You can mark it paid later.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
                    No per-case rate configured for this visa program. Officer will be assigned without a payment record. Update the rate in Staff settings.
                  </div>
                )}
              </div>

              <div className="px-5 py-4 border-t border-slate-200 flex gap-3">
                <button onClick={() => setContractorDialog(null)} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 text-sm">Cancel</button>
                <button onClick={handleContractorAssignConfirm} disabled={savingContractorPay} className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                  {savingContractorPay ? <><Loader2 size={14} className="animate-spin" />{contractorDialog?.logFeeOnly ? 'Saving…' : 'Assigning…'}</> : <><CheckCircle size={14} />{contractorDialog?.logFeeOnly ? 'Log Payment' : 'Assign & Log Payment'}</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
